import { renderPhoneNumber } from "@portal/app/_common/lib/formatRenderers";

/**
 * Escapes HTML special characters to prevent XSS when substituted values
 * are rendered via dangerouslySetInnerHTML (e.g., DreamMarkdownReader).
 */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Resolves {{field_key}} template tokens in a string by substituting
 * values from the provided answers map.
 *
 * - Key lookup is case-insensitive so {{phone}}, {{PHONE}}, and {{Phone}} all match.
 * - Whitespace inside braces is tolerated: {{ phone_number }} works like {{phone_number}}.
 * - If a key is found in answers, its value is substituted (formatted for phone numbers).
 * - If a key is NOT found, the token is left as-is (safe fallback for compliance).
 * - Substituted values are HTML-escaped to prevent XSS.
 * - Supports any field key that exists in the form answers.
 *
 * @example
 * resolveTemplateVariables("Phone: {{phone_number}}", { phone_number: "2025551234" })
 * // => "Phone: (202) 555-1234"
 */
export function resolveTemplateVariables(
  template: string,
  answers: Record<string, unknown>
): string {
  if (!template) return template;

  // Build a case-insensitive lookup map so {{phone}} matches a "PHONE" key
  const lowerCaseMap = new Map<string, unknown>();
  for (const [k, v] of Object.entries(answers)) {
    lowerCaseMap.set(k.toLowerCase(), v);
  }

  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, key: string) => {
    const value = lowerCaseMap.get(key.toLowerCase());
    if (value == null || value === "") return _match; // Leave token as-is if no value

    const stringValue = String(value);

    // Format phone numbers when the key contains "phone"
    if (key.toLowerCase().includes("phone")) {
      return escapeHtml(renderPhoneNumber(stringValue));
    }

    return escapeHtml(stringValue);
  });
}
